import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { Button, CircularProgress } from '@mui/material';
import { UserInterface } from '../types';
import { countryCodes, DEFAULT_COUNTRY_CODE, formatFullPhoneNumber } from '../utils/countryCodes';
import ReactGA from 'react-ga4';
import { updateUserProfile } from '../redux/actions';

const Wrapper = styled.div`
	min-height: 100vh;
	padding: 50px 50px;
	background-color: white;

	@media (max-width: 768px) {
		padding: 20px;
		padding-top: 90px;
	}
`;

const FormGroup = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
`;

const InputField = styled.input`
	background-color: #f9fafb;
	border: 1px solid #dee1e5;
	padding: 12px 16px;
	border-radius: 8px;
	width: 100%;
	font-size: 16px;
	transition: border-color 0.2s;

	&:focus {
		border-color: #3a5bd9;
		outline: none;
		box-shadow: 0 0 0 3px rgba(58, 91, 217, 0.2);
	}

	&:disabled {
		background-color: #f3f3f3;
		cursor: not-allowed;
	}
`;

const Label = styled.label`
	font-size: 14px;
	font-weight: 500;
	margin-bottom: 8px;
`;

const FormWrapper = styled.div`
	width: 100%;
	max-width: 500px;
`;

const Title = styled.h1`
	font-size: 28px;
	font-weight: 700;
	margin-bottom: 8px;
	color: #111827;
`;

const Description = styled.p`
	font-size: 16px;
	color: #6b7280;
	margin-bottom: 24px;
`;

const Divider = styled.hr`
	border-top: 1px solid #e5e7eb;
	width: 100%;
	margin: 24px 0;
`;

const SaveButton = styled(Button)`
	margin-top: 8px !important;
`;

const PhoneContainer = styled.div`
	display: flex;
	gap: 0;
`;

const CountryCodeSelect = styled.select`
	background-color: #f9fafb;
	border: 1px solid #dee1e5;
	border-right: none;
	padding: 12px 8px;
	border-radius: 8px 0 0 8px;
	font-size: 16px;
	min-width: 100px;
	transition: border-color 0.2s;

	&:focus {
		border-color: #3a5bd9;
		outline: none;
		box-shadow: 0 0 0 3px rgba(58, 91, 217, 0.2);
	}

	&:disabled {
		background-color: #f3f3f3;
		cursor: not-allowed;
	}
`;

const PhoneInput = styled(InputField)`
	border-radius: 0 8px 8px 0;
	border-left: none;
	flex: 1;

	&:focus {
		border-color: #3a5bd9;
		outline: none;
		box-shadow: 0 0 0 3px rgba(58, 91, 217, 0.2);
	}
`;

const PhoneNote = styled.p`
	font-size: 12px;
	color: #6b7280;
	margin-top: 4px;
	margin-bottom: 0;
`;

export default function Account() {
	const user = useSelector((state: any) => state.auth.user);
	const dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [formData, setFormData] = useState<UserInterface>({
		firstname: '',
		surname: '',
		email: '',
		phone: '',
		countryCode: DEFAULT_COUNTRY_CODE,
	});

	// Initialize form data from Redux state
	useEffect(() => {
		if (user) {
			// Parse existing phone number to extract country code and phone
			let countryCode = DEFAULT_COUNTRY_CODE;
			let phone = '';

			if (user.phone) {
				// Find matching country code
				const matchedCountry = countryCodes.find((country) => user.phone?.startsWith(country.code));

				if (matchedCountry) {
					countryCode = matchedCountry.code;
					phone = user.phone.substring(matchedCountry.code.length);
				} else {
					// If no country code found, assume it's just the phone number
					phone = user.phone;
				}
			}

			setFormData({
				firstname: user.firstname || '',
				surname: user.surname || '',
				email: user.email || '',
				phone: phone,
				countryCode: countryCode,
			});
		}

		ReactGA.send({
			hitType: 'pageview',
			page: '/account',
			title: 'Account Settings',
		});
	}, [user]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;

		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.firstname || !formData.surname || !formData.email) {
			notify('error', 'All required fields must be filled');
			return;
		}

		setIsLoading(true);

		try {
			// Format phone number if provided
			const fullPhoneNumber =
				formData.phone && formData.countryCode
					? formatFullPhoneNumber(formData.countryCode, formData.phone)
					: null;

			// Send update request to the API
			const response = await apiClient.update(
				'/users/profile',
				{
					firstname: formData.firstname,
					surname: formData.surname,
					email: formData.email,
					phone: fullPhoneNumber,
				},
				true
			);

			// Only update Redux state if the API update was successful
			if (response) {
				dispatch(
					updateUserProfile({
						firstname: formData.firstname,
						surname: formData.surname,
						email: formData.email,
						phone: fullPhoneNumber,
					})
				);

				notify('success', 'Profile updated successfully');
			}
		} catch (error) {
			console.error('Error updating profile:', error);
		} finally {
			setIsLoading(false);
		}
	};

	if (!user) {
		return (
			<Wrapper>
				<CircularProgress />
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<FormWrapper>
				<Title>Account Settings</Title>
				<Description>Update your personal information</Description>

				<Divider />

				<form onSubmit={handleSubmit}>
					<FormGroup>
						<Label htmlFor="firstname">First Name</Label>
						<InputField
							id="firstname"
							name="firstname"
							type="text"
							value={formData.firstname}
							onChange={handleChange}
							placeholder="Enter your first name"
							required
						/>
					</FormGroup>

					<FormGroup>
						<Label htmlFor="surname">Last Name</Label>
						<InputField
							id="surname"
							name="surname"
							type="text"
							value={formData.surname}
							onChange={handleChange}
							placeholder="Enter your last name"
							required
						/>
					</FormGroup>

					<FormGroup>
						<Label htmlFor="email">Email Address</Label>
						<InputField
							id="email"
							name="email"
							type="email"
							value={formData.email}
							onChange={handleChange}
							placeholder="Enter your email address"
							required
						/>
					</FormGroup>

					<FormGroup>
						<Label htmlFor="phone">Phone Number</Label>
						<PhoneContainer>
							<CountryCodeSelect
								name="countryCode"
								value={formData.countryCode}
								onChange={handleChange}
								disabled={isLoading}
							>
								{countryCodes.map((country) => (
									<option key={country.code} value={country.code}>
										{country.flag} {country.code}
									</option>
								))}
							</CountryCodeSelect>
							<PhoneInput
								id="phone"
								name="phone"
								type="tel"
								value={formData.phone || ''}
								onChange={handleChange}
								placeholder="e.g. 554123456"
								disabled={isLoading}
							/>
						</PhoneContainer>
						<PhoneNote>Enter your phone number without spaces or country code</PhoneNote>
					</FormGroup>

					<SaveButton
						variant="contained"
						color="primary"
						type="submit"
						fullWidth
						disabled={isLoading}
						disableElevation
					>
						{isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
					</SaveButton>
				</form>
			</FormWrapper>
		</Wrapper>
	);
}
